/**
 * Named photo-adjustment presets. Previewed with the shared SVG filter in the editor and baked into
 * the export derivatives by the worker (`@freealbum/document` photo-pipeline), so the numbers must
 * stay inside the schema's ranges.
 */
import { Adjustments, neutralAdjustments } from '@freealbum/document';
import type { AdjustmentPreset } from './types';

const preset = (id: string, values: Partial<Omit<Adjustments, 'preset'>>): AdjustmentPreset => ({
  id,
  nameKey: `adjustments.${id}`,
  adjustments: Adjustments.parse({ ...neutralAdjustments(), ...values, preset: id === 'none' ? null : id }),
});

export const ADJUSTMENT_PRESETS: readonly AdjustmentPreset[] = [
  preset('none', {}),
  // colour
  preset('warm', { temperature: 0.35, brightness: 1.03, saturation: 1.08 }),
  preset('cool', { temperature: -0.35, contrast: 1.04, saturation: 0.95 }),
  preset('vivid', { contrast: 1.12, saturation: 1.3, clarity: 0.25 }),
  preset('punch', { contrast: 1.2, saturation: 1.15, clarity: 0.4, blacks: -0.3 }),
  preset('crisp', { clarity: 0.5, sharpen: 0.4, contrast: 1.05, saturation: 1.05 }),
  // soft / filmic
  preset('matte', { contrast: 0.9, blacks: 0.6, whites: -0.2, saturation: 0.9 }),
  preset('fade', { brightness: 1.05, contrast: 0.85, blacks: 0.8, saturation: 0.75 }),
  preset('soft', { contrast: 0.95, highlights: -0.3, shadows: 0.3, saturation: 0.95 }),
  preset('film', { contrast: 1.06, blacks: 0.35, highlights: -0.25, temperature: 0.1, saturation: 0.92, vignette: 0.2 }),
  preset('vintage', { temperature: 0.3, blacks: 0.6, contrast: 0.92, saturation: 0.85, vignette: 0.5 }),
  preset('moody', { contrast: 1.15, shadows: -0.4, blacks: -0.3, saturation: 0.8, temperature: -0.15, vignette: 0.6 }),
  // monochrome
  preset('bw-classic', { grayscale: 1, contrast: 1.05 }),
  preset('bw-high-contrast', { grayscale: 1, contrast: 1.3, blacks: -0.4, whites: 0.3, clarity: 0.3 }),
  preset('bw-soft', { grayscale: 1, contrast: 0.9, blacks: 0.5 }),
  preset('sepia', { sepia: 1, saturation: 0.85, contrast: 1.02 }),
];

const BY_ID = new Map(ADJUSTMENT_PRESETS.map((p) => [p.id, p]));

export function getAdjustmentPreset(id: string | null): AdjustmentPreset | undefined {
  return id === null ? BY_ID.get('none') : BY_ID.get(id);
}

/** Neutral adjustments — the value a fresh photo element starts from. */
export const NEUTRAL_ADJUSTMENTS = ADJUSTMENT_PRESETS[0]!.adjustments;
